import { FC } from 'react';
import cx from 'classnames';
import { useQuery } from '@tanstack/react-query';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import { useInjuryEndpoints } from '../../../hooks/useInjuryEndpoints';
import { TEAM_INJURY_METRIC } from '../../../enums/injuries';
import {
  ITeamCapabilities,
  ITeamTimelineMetric,
  ITeamBodyMapMetric,
  ITeamTypeBreakdownMetric,
  ITeamMechanismMetric,
  ITeamSeverityMetric,
  ITeamLeaderboardMetric,
  ITeamRecurrenceMetric,
  ITeamMonthlyRateMetric,
  ITeamReturnConfidenceMetric,
  ITeamRtpStatusMetric,
  ITeamAcwrMetric,
  ITeamHooperSeriesMetric,
  ITeamReadinessIndexMetric,
  ITeamCmjRsiSeriesMetric,
} from '../../../models/injury';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import { TeamTimelineChart } from './charts/TeamTimelineChart';
import { TeamBodyMapChart } from './charts/TeamBodyMapChart';
import { TeamTypeBreakdownChart } from './charts/TeamTypeBreakdownChart';
import { TeamMechanismChart } from './charts/TeamMechanismChart';
import { TeamSeverityChart } from './charts/TeamSeverityChart';
import { TeamLeaderboardChart } from './charts/TeamLeaderboardChart';
import { TeamRecurrenceChart } from './charts/TeamRecurrenceChart';
import { TeamIncidenceByRoleChart } from './charts/TeamIncidenceByRoleChart';
import { TeamCurrentlyInjuredCard } from './charts/TeamCurrentlyInjuredCard';
import { TeamMonthlyRateChart } from './charts/TeamMonthlyRateChart';
import { TeamReturnConfidenceCard } from './charts/TeamReturnConfidenceCard';
import { TeamRtpStatusCard } from './charts/TeamRtpStatusCard';
import { TeamAcwrChart } from './charts/TeamAcwrChart';
import { TeamHooperSeriesChart } from './charts/TeamHooperSeriesChart';
import { TeamReadinessIndexChart } from './charts/TeamReadinessIndexChart';
import { TeamCmjRsiSeriesChart } from './charts/TeamCmjRsiSeriesChart';
import styles from './TeamInjuryCharts.module.scss';

interface TeamInjuryChartsProps {
  teamId: string;
  period?: string;
}

/**
 * Card di un singolo chart nella griglia: header col titolo + corpo. Durante
 * il caricamento mostra il loader al posto del contenuto (mai "Nessun dato"
 * mentre la query è ancora pending — quello è lo stato empty, distinto).
 */
const ChartCard: FC<{ title: string; children: React.ReactNode; fullWidth?: boolean; isLoading?: boolean }> = ({
  title,
  children,
  fullWidth,
  isLoading,
}) => (
  <div className={cx(styles.chartCard, { [styles.chartCardFull]: fullWidth })}>
    <div className={styles.chartCardHead}>
      <span className={styles.chartCardTitle}>{title}</span>
    </div>
    {isLoading ? (
      <div className={styles.chartCardLoading}>
        <LeopartLoader size="sm" />
      </div>
    ) : (
      children
    )}
  </div>
);

function useTeamMetric<T>(
  teamId: string,
  metric: TEAM_INJURY_METRIC,
  period: string | undefined,
  enabled: boolean,
) {
  const endpoints = useInjuryEndpoints();
  return useQuery<T>({
    queryKey: ['injury', 'team', teamId, 'metric', metric, period ?? 'all'],
    queryFn: async () => {
      const { data } = await endpoints.getTeamInjuryMetric(teamId, metric, period ? { period } : undefined);
      return data as T;
    },
    enabled,
    staleTime: 1000 * 60 * 5,
  });
}

export const TeamInjuryCharts: FC<TeamInjuryChartsProps> = ({ teamId, period }) => {
  const endpoints = useInjuryEndpoints();

  const { data: capabilities, isLoading: capsLoading, isError: capsError, refetch: capsRefetch } =
    useQuery<ITeamCapabilities>({
      queryKey: ['injury', 'team', teamId, 'capabilities'],
      queryFn: async () => {
        const { data } = await endpoints.getTeamCapabilities(teamId);
        return data;
      },
      staleTime: 1000 * 60 * 10,
    });

  const has = (m: TEAM_INJURY_METRIC) => capabilities?.[m] === true;

  const timelineQ    = useTeamMetric<ITeamTimelineMetric>(teamId, TEAM_INJURY_METRIC.TIMELINE, period, has(TEAM_INJURY_METRIC.TIMELINE));

  // Currently injured: filtriamo dai dati timeline (hanno firstName/lastName)
  const activeInjuries = timelineQ.data?.data?.filter((d) => d.status === 'ACTIVE') ?? null;
  const bodyMapQ     = useTeamMetric<ITeamBodyMapMetric>(teamId, TEAM_INJURY_METRIC.BODY_MAP, period, has(TEAM_INJURY_METRIC.BODY_MAP));
  const typeQ        = useTeamMetric<ITeamTypeBreakdownMetric>(teamId, TEAM_INJURY_METRIC.TYPE_BREAKDOWN, period, has(TEAM_INJURY_METRIC.TYPE_BREAKDOWN));
  const mechanismQ   = useTeamMetric<ITeamMechanismMetric>(teamId, TEAM_INJURY_METRIC.MECHANISM_BREAKDOWN, period, has(TEAM_INJURY_METRIC.MECHANISM_BREAKDOWN));
  const severityQ    = useTeamMetric<ITeamSeverityMetric>(teamId, TEAM_INJURY_METRIC.SEVERITY_BREAKDOWN, period, has(TEAM_INJURY_METRIC.SEVERITY_BREAKDOWN));
  const leaderboardQ = useTeamMetric<ITeamLeaderboardMetric>(teamId, TEAM_INJURY_METRIC.LEADERBOARD, period, has(TEAM_INJURY_METRIC.LEADERBOARD));
  const recurrenceQ  = useTeamMetric<ITeamRecurrenceMetric>(teamId, TEAM_INJURY_METRIC.RECURRENCE_BY_POSITION, period, has(TEAM_INJURY_METRIC.RECURRENCE_BY_POSITION));
  const monthlyQ     = useTeamMetric<ITeamMonthlyRateMetric>(teamId, TEAM_INJURY_METRIC.MONTHLY_RATE, period, has(TEAM_INJURY_METRIC.MONTHLY_RATE));
  const retConfQ     = useTeamMetric<ITeamReturnConfidenceMetric>(teamId, TEAM_INJURY_METRIC.RETURN_CONFIDENCE_AVG, period, has(TEAM_INJURY_METRIC.RETURN_CONFIDENCE_AVG));
  const rtpQ         = useTeamMetric<ITeamRtpStatusMetric>(teamId, TEAM_INJURY_METRIC.RTP_STATUS, period, has(TEAM_INJURY_METRIC.RTP_STATUS));
  const acwrQ        = useTeamMetric<ITeamAcwrMetric>(teamId, TEAM_INJURY_METRIC.ACWR_SERIES, period, has(TEAM_INJURY_METRIC.ACWR_SERIES));
  const hooperTeamQ  = useTeamMetric<ITeamHooperSeriesMetric>(teamId, TEAM_INJURY_METRIC.HOOPER_SERIES, period, has(TEAM_INJURY_METRIC.HOOPER_SERIES));
  const riTeamQ      = useTeamMetric<ITeamReadinessIndexMetric>(teamId, TEAM_INJURY_METRIC.READINESS_INDEX, period, has(TEAM_INJURY_METRIC.READINESS_INDEX));
  const cmjRsiTeamQ  = useTeamMetric<ITeamCmjRsiSeriesMetric>(teamId, TEAM_INJURY_METRIC.CMJ_RSI_SERIES, period, has(TEAM_INJURY_METRIC.CMJ_RSI_SERIES));

  if (capsLoading) {
    return (
      <div className={styles.loadingState}>
        <LeopartLoader size="sm" />
        <span className={styles.loadingText}>Caricamento grafici...</span>
      </div>
    );
  }

  if (capsError) {
    return <ChartErrorState onRetry={capsRefetch} message="Impossibile verificare le metriche disponibili" />;
  }

  if (!capabilities || !Object.values(capabilities).some(Boolean)) return null;

  return (
    <div>
      <h3 className={styles.sectionTitle}>Analisi infortuni team</h3>

      <div className={styles.grid}>
        {/* 1. Timeline — full width */}
        {has(TEAM_INJURY_METRIC.TIMELINE) && (
          <ChartCard title="Timeline infortuni team" fullWidth isLoading={timelineQ.isLoading}>
            {timelineQ.isError
              ? <ChartErrorState onRetry={timelineQ.refetch} message={(timelineQ.error as Error)?.message} />
              : <TeamTimelineChart data={timelineQ.data ?? null} />}
          </ChartCard>
        )}

        {/* 2. ACWR — full width */}
        {has(TEAM_INJURY_METRIC.ACWR_SERIES) && (
          <ChartCard title="ACWR medio team" fullWidth isLoading={acwrQ.isLoading}>
            {acwrQ.isError
              ? <ChartErrorState onRetry={acwrQ.refetch} message={(acwrQ.error as Error)?.message} />
              : <TeamAcwrChart data={acwrQ.data ?? null} />}
          </ChartCard>
        )}

        {/* Hooper series team */}
        {has(TEAM_INJURY_METRIC.HOOPER_SERIES) && (
          <ChartCard title="Questionario Hooper — trend team" fullWidth isLoading={hooperTeamQ.isLoading}>
            {hooperTeamQ.isError
              ? <ChartErrorState onRetry={hooperTeamQ.refetch} message={(hooperTeamQ.error as Error)?.message} />
              : <TeamHooperSeriesChart data={hooperTeamQ.data ?? null} />}
          </ChartCard>
        )}

        {/* Readiness index team */}
        {has(TEAM_INJURY_METRIC.READINESS_INDEX) && (
          <ChartCard title="Indice di Readiness — media team" fullWidth isLoading={riTeamQ.isLoading}>
            {riTeamQ.isError
              ? <ChartErrorState onRetry={riTeamQ.refetch} message={(riTeamQ.error as Error)?.message} />
              : <TeamReadinessIndexChart data={riTeamQ.data ?? null} />}
          </ChartCard>
        )}

        {/* CMJ / RSI team */}
        {has(TEAM_INJURY_METRIC.CMJ_RSI_SERIES) && (
          <ChartCard title="CMJ / RSI — media team" fullWidth isLoading={cmjRsiTeamQ.isLoading}>
            {cmjRsiTeamQ.isError
              ? <ChartErrorState onRetry={cmjRsiTeamQ.refetch} message={(cmjRsiTeamQ.error as Error)?.message} />
              : <TeamCmjRsiSeriesChart data={cmjRsiTeamQ.data ?? null} />}
          </ChartCard>
        )}

        {/* 8. Currently injured — full width (derivato dalla timeline, stessa query) */}
        {has(TEAM_INJURY_METRIC.TIMELINE) && (timelineQ.isLoading || (activeInjuries?.length ?? 0) > 0) && (
          <ChartCard title="Atleti attualmente infortunati" fullWidth isLoading={timelineQ.isLoading}>
            <TeamCurrentlyInjuredCard data={activeInjuries} />
          </ChartCard>
        )}

        {/* 6. Leaderboard giorni persi */}
        {has(TEAM_INJURY_METRIC.LEADERBOARD) && (
          <ChartCard title="Top 5 atleti per giorni persi" isLoading={leaderboardQ.isLoading}>
            {leaderboardQ.isError
              ? <ChartErrorState onRetry={leaderboardQ.refetch} message={(leaderboardQ.error as Error)?.message} />
              : <TeamLeaderboardChart data={leaderboardQ.data ?? null} />}
          </ChartCard>
        )}

        {/* 9. Tasso mensile */}
        {has(TEAM_INJURY_METRIC.MONTHLY_RATE) && (
          <ChartCard title="Infortuni mensili" isLoading={monthlyQ.isLoading}>
            {monthlyQ.isError
              ? <ChartErrorState onRetry={monthlyQ.refetch} message={(monthlyQ.error as Error)?.message} />
              : <TeamMonthlyRateChart data={monthlyQ.data ?? null} />}
          </ChartCard>
        )}

        {/* 2. Body map */}
        {has(TEAM_INJURY_METRIC.BODY_MAP) && (
          <ChartCard title="Zone più colpite" isLoading={bodyMapQ.isLoading}>
            {bodyMapQ.isError
              ? <ChartErrorState onRetry={bodyMapQ.refetch} message={(bodyMapQ.error as Error)?.message} />
              : <TeamBodyMapChart data={bodyMapQ.data ?? null} />}
          </ChartCard>
        )}

        {/* 3. Tipo infortunio */}
        {has(TEAM_INJURY_METRIC.TYPE_BREAKDOWN) && (
          <ChartCard title="Tipo infortunio" isLoading={typeQ.isLoading}>
            {typeQ.isError
              ? <ChartErrorState onRetry={typeQ.refetch} message={(typeQ.error as Error)?.message} />
              : <TeamTypeBreakdownChart data={typeQ.data ?? null} />}
          </ChartCard>
        )}

        {/* 4. Meccanismo */}
        {has(TEAM_INJURY_METRIC.MECHANISM_BREAKDOWN) && (
          <ChartCard title="Meccanismo infortunio" isLoading={mechanismQ.isLoading}>
            {mechanismQ.isError
              ? <ChartErrorState onRetry={mechanismQ.refetch} message={(mechanismQ.error as Error)?.message} />
              : <TeamMechanismChart data={mechanismQ.data ?? null} />}
          </ChartCard>
        )}

        {/* 5. Severity */}
        {has(TEAM_INJURY_METRIC.SEVERITY_BREAKDOWN) && (
          <ChartCard title="Distribuzione severità" isLoading={severityQ.isLoading}>
            {severityQ.isError
              ? <ChartErrorState onRetry={severityQ.refetch} message={(severityQ.error as Error)?.message} />
              : <TeamSeverityChart data={severityQ.data ?? null} />}
          </ChartCard>
        )}

        {/* 7. Tasso ricorrenza */}
        {has(TEAM_INJURY_METRIC.RECURRENCE_BY_POSITION) && (
          <ChartCard title="Ricorrenza per posizione" isLoading={recurrenceQ.isLoading}>
            {recurrenceQ.isError
              ? <ChartErrorState onRetry={recurrenceQ.refetch} message={(recurrenceQ.error as Error)?.message} />
              : <TeamRecurrenceChart data={recurrenceQ.data ?? null} />}
          </ChartCard>
        )}

        {/* 7.12. Incidenza infortuni per ruolo */}
        {has(TEAM_INJURY_METRIC.RECURRENCE_BY_POSITION) && (
          <ChartCard title="Incidenza infortuni per ruolo" isLoading={recurrenceQ.isLoading}>
            {recurrenceQ.isError
              ? <ChartErrorState onRetry={recurrenceQ.refetch} message={(recurrenceQ.error as Error)?.message} />
              : <TeamIncidenceByRoleChart data={recurrenceQ.data ?? null} />}
          </ChartCard>
        )}

        {/* 10. Fiducia ritorno media */}
        {has(TEAM_INJURY_METRIC.RETURN_CONFIDENCE_AVG) && (
          <ChartCard title="Fiducia ritorno media" isLoading={retConfQ.isLoading}>
            {retConfQ.isError
              ? <ChartErrorState onRetry={retConfQ.refetch} message={(retConfQ.error as Error)?.message} />
              : <TeamReturnConfidenceCard data={retConfQ.data ?? null} />}
          </ChartCard>
        )}

        {/* 11. Stato RTP */}
        {has(TEAM_INJURY_METRIC.RTP_STATUS) && (
          <ChartCard title="Stato protocollo RTP" isLoading={rtpQ.isLoading}>
            {rtpQ.isError
              ? <ChartErrorState onRetry={rtpQ.refetch} message={(rtpQ.error as Error)?.message} />
              : <TeamRtpStatusCard data={rtpQ.data ?? null} />}
          </ChartCard>
        )}
      </div>
    </div>
  );
};
